// src/pages/InboxPage.js
import React, { useState, useEffect } from 'react'
import ChatPage from './ChatPage'
import NotificationPage from './NotificationPage'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, PlusCircle, X, Search } from 'lucide-react'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../firebaseConfig'

export default function InboxPage() {
  const [activeTab, setActiveTab] = useState('chat')
  const navigate = useNavigate()

  // === STATE UNTUK MODAL ===
  const [showModal, setShowModal] = useState(false)
  const [modalTab, setModalTab] = useState('user') // 'user' atau 'mitra'
  const [searchQ, setSearchQ] = useState('')
  const [listUsers, setListUsers] = useState([])
  const [listMitra, setListMitra] = useState([])
  const [loadingModal, setLoadingModal] = useState(false)
  
  // State untuk mengirim kontak yang dipilih ke ChatPage
  const [contactToOpen, setContactToOpen] = useState(null)

  // Fetch data ketika modal dibuka
  useEffect(() => {
    if (showModal && listUsers.length === 0 && listMitra.length === 0) {
      fetchContacts()
    }
  }, [showModal])

  const fetchContacts = async () => {
    setLoadingModal(true)
    try {
      // 1. Fetch Users
      const us = await getDocs(collection(db, 'users'))
      const usersData = us.docs.map((d) => ({
        id: d.id,
        name: d.data().name || d.data().displayName || d.data().username || 'User Tanpa Nama',
        email: d.data().email || '',
        type: 'user'
      }))
      setListUsers(usersData)

      // 2. Fetch Mitras
      const th = await getDocs(collection(db, 'therapists'))
      const cl = await getDocs(collection(db, 'cleaners'))
      const dr = await getDocs(collection(db, 'drivers'))

      const mitraData = [
        ...th.docs.map(d => ({ id: d.data().id_number, name: d.data().name, type: 'mitra' })),
        ...cl.docs.map(d => ({ id: d.data().id_number, name: d.data().name, type: 'mitra' })),
        ...dr.docs.map(d => ({ id: d.id, name: d.data().name || 'Driver Zimo', type: 'mitra' })),
      ]
      setListMitra(mitraData)
    } catch (err) {
      console.error("Gagal mengambil data kontak:", err)
    } finally {
      setLoadingModal(false)
    }
  }

  // Filter pencarian di dalam modal
  const targetModalList = modalTab === 'user' ? listUsers : listMitra
  const filteredModalList = targetModalList.filter((c) =>
    c.name.toLowerCase().includes(searchQ.toLowerCase())
  )

  const handleSelectContact = (contact) => {
    // Kirim kontak ke ChatPage dan tutup modal
    setContactToOpen(contact)
    setShowModal(false)
    setSearchQ('')
  }

  return (
    <div className="p-6 bg-gray-950 text-gray-200 min-h-screen relative">
      {/* 🔹 HEADER UTAMA */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
            title="Kembali"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-200">Kotak Masuk</h1>
            <p className="text-sm text-gray-600">
              Kelola percakapan dan notifikasi dengan mitra dan pengguna.
            </p>
          </div>
        </div>
      </div>

      {/* 🔹 TAB SELECTOR & ACTION */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex bg-gray-900 rounded-full w-fit p-1 border border-gray-800">
          <button
            onClick={() => setActiveTab('chat')}
            className={`px-6 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
              activeTab === 'chat'
                ? 'bg-gray-800 text-gray-200 shadow'
                : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
            }`}
          >
            Chat
          </button>
          <button
            onClick={() => setActiveTab('notifikasi')}
            className={`px-6 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
              activeTab === 'notifikasi'
                ? 'bg-gray-800 text-gray-200 shadow'
                : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
            }`}
          >
            Notifikasi
          </button>
        </div>

        {/* 🔹 TOMBOL TULIS PESAN */}
        {activeTab === 'chat' && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shadow-lg"
          >
            <PlusCircle size={18} />
            Tulis Pesan
          </button>
        )}
      </div>

      {/* 🔹 CONTENT AREA */}
      <div className="bg-gray-900 rounded-xl shadow border border-gray-800 p-5 min-h-[60vh] transition-all duration-300">
        {activeTab === 'chat' ? (
          <ChatPage contactToOpen={contactToOpen} />
        ) : (
          <NotificationPage />
        )}
      </div>

      {/* ==================================================== */}
      {/* 🔹 MODAL PILIH KONTAK (POP-UP) */}
      {/* ==================================================== */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
            
            {/* Header Modal */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-gray-800/50">
              <h2 className="text-lg font-semibold text-gray-200">Mulai Chat Baru</h2>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-200 p-1 rounded-full hover:bg-gray-700 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body Modal */}
            <div className="p-4 flex flex-col gap-4">
              {/* Tab User / Mitra */}
              <div className="flex bg-gray-800 p-1 rounded-lg">
                <button
                  onClick={() => setModalTab('user')}
                  className={`flex-1 py-1.5 text-sm font-medium rounded-md transition ${modalTab === 'user' ? 'bg-gray-700 text-white shadow' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  Pelanggan
                </button>
                <button
                  onClick={() => setModalTab('mitra')}
                  className={`flex-1 py-1.5 text-sm font-medium rounded-md transition ${modalTab === 'mitra' ? 'bg-gray-700 text-white shadow' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  Mitra (Pekerja)
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input 
                  type="text"
                  placeholder="Cari nama..."
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-blue-500 transition"
                />
              </div>

              {/* List Kontak */}
              <div className="overflow-y-auto max-h-60 border border-gray-800 rounded-lg bg-gray-950/50">
                {loadingModal ? (
                  <div className="p-4 text-center text-sm text-gray-500">Memuat data...</div>
                ) : filteredModalList.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">Tidak ada hasil.</div>
                ) : (
                  filteredModalList.map(contact => (
                    <div 
                      key={contact.id}
                      onClick={() => handleSelectContact(contact)}
                      className="p-3 border-b border-gray-800/50 hover:bg-gray-800 cursor-pointer flex justify-between items-center transition"
                    >
                      <div className="overflow-hidden">
                        <div className="font-medium text-gray-200 truncate">{contact.name}</div>
                        <div className="text-xs text-gray-500 truncate">{contact.email || contact.id}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  )
}